import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import withLoading from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PendingChangesActionType } from 'constants/pendingChanges';
import usePopover from 'hooks/usePopover';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyTaxesInfo } from '../PropertyTaxes.constants';

const defaultObject = {};

const ActionButton = ({
  rowData = defaultObject,
  setEditableData = _.noop,
  deleteTableData = _.noop,
  isEditDisabled = false,
  isDisabled = false,
}) => {
  const dispatch = useDispatch();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isNewAdded = rowData.pendingChangesActionType === PendingChangesActionType.ADDED;

  const onClickEdit = useCallback(() => {
    onClearAnchorElement();

    setEditableData({ ...rowData, isEdited: true });
  }, [onClearAnchorElement, rowData, setEditableData]);

  const onClickRemove = useCallback(() => {
    onClearAnchorElement();

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: `Remove ${PropertyTaxesInfo.TITLE}`,
        description: `Are you sure you want to remove this ${PropertyTaxesInfo.TITLE}?`,
        onAccept: () => {
          deleteTableData(rowData.id);
        },
      }),
    );
  }, [deleteTableData, dispatch, onClearAnchorElement, rowData.id]);

  const items = useMemo(
    () => [
      {
        text: 'Edit',
        onClick: onClickEdit,
        icon: <EditPenIcon size={IconSize[20]} />,
        isDisabled: isEditDisabled,
      },
      {
        text: 'Delete',
        onClick: onClickRemove,
        icon: <DeleteIcon size={IconSize[20]} />,
        isDisabled: isNewAdded || rowData.isPrimary,
      },
    ],
    [isEditDisabled, isNewAdded, onClickEdit, onClickRemove, rowData.isPrimary],
  );

  return (
    <>
      <MoreButtonCDS
        disabled={isDisabled}
        isHighlighted={isNewAdded}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

ActionButton.propTypes = {
  setEditableData: PropTypes.func,
  deleteTableData: PropTypes.func,
  isEditDisabled: PropTypes.bool,
  rowData: PropTypes.object,
  isDisabled: PropTypes.bool,
};

export default withLoading(ActionButton);
